import { NO_SHARED_NAMEPLATES } from '@rock/brands';
import { countQueryFlags, lessonQueryFlags, queryFlags, type QueryFlag } from '@/app/considerations/appliesToStatus';
import { BrandSearchIndexDao, CustomEquipmentTypeDao, SpaceDao, SystemInfoDao } from '@rock/db-admin';
import { buildHomeDocument, type HqlCatalog } from '@rock/hql';
import {
    type ConsiderationTypeId,
    finishedSpaceIds,
    type LessonTypeId,
    type TaskType,
    type Timestamp,
} from '@rock/types';
import { atom } from 'jotai';
import { groupBy } from 'lodash';
import { considerationTypesState } from './considerationTypes.state';
import { allHomeEquipmentState } from './equipment.state';
import { currentHomeIdState, currentHomeState } from './home.state';
import { allEquipmentTypesState, allInfoTypesState } from './itemTypes.state';
import { lessonTypesState } from './lessonTypes.state';
import { taskTypesState } from './taskTypes.state';

const spaceDao = new SpaceDao();
const brandSearchIndexDao = new BrandSearchIndexDao();
const systemInfoDao = new SystemInfoDao();
const customEquipmentTypeDao = new CustomEquipmentTypeDao();

/** Published spaces only, as on the server: an unpublished space is one no home can complete. */
const publishedSpacesState = spaceDao.spacesAtom('published');

/** The published brand index. Read once per page load: only a rebuild changes it. */
const brandSearchIndexState = atom(async () => await brandSearchIndexDao.getBrandSearchIndex());

/**
 * The catalog consideration and lesson queries are checked against, built from the same sources the
 * server uses (`@rock/db-server` `HqlReader.fetchCatalog`), so admin and server agree on which queries
 * are valid. Catalog types only: a catalog type's query may never name one home's custom type.
 */
export const hqlCatalogState = atom(async (get): Promise<HqlCatalog> => {
    const [equipmentTypes, infoTypes, spaces, index] = await Promise.all([
        get(allEquipmentTypesState),
        get(allInfoTypesState),
        get(publishedSpacesState),
        get(brandSearchIndexState),
    ]);
    return {
        equipmentTypes,
        infoTypes,
        spaceIds: spaces.map((space) => space.id),
        brands: index?.entries ?? [],
        sharedNameplates: index?.sharedNameplates ?? NO_SHARED_NAMEPLATES,
    };
});

/** The `updatedAt` each of this session's draft requests wrote, by type id, so the buttons know which are on their way. */
export const appliesToDraftRequestsState = atom<Partial<Record<ConsiderationTypeId | LessonTypeId, Timestamp>>>({});

/** Every consideration type's own task types, from one listener. */
export const taskTypesByConsiderationTypeState = atom(
    async (get): Promise<Record<ConsiderationTypeId, TaskType[]>> =>
        groupBy(await get(taskTypesState), (taskType) => taskType.considerationTypeId),
);

/** Each consideration type's query flags (see `queryFlags`), by type id. */
export const considerationQueryFlagsState = atom(async (get) => {
    const [types, taskTypesByType, catalog] = await Promise.all([
        get(considerationTypesState),
        get(taskTypesByConsiderationTypeState),
        get(hqlCatalogState),
    ]);
    const flags: Record<ConsiderationTypeId, QueryFlag[]> = {};
    for (const type of types) flags[type.id] = queryFlags(type, taskTypesByType[type.id] ?? [], catalog);
    return flags;
});

/** Each lesson type's query flags (see `lessonQueryFlags`), by type id. */
export const lessonQueryFlagsState = atom(async (get) => {
    const [types, catalog] = await Promise.all([get(lessonTypesState), get(hqlCatalogState)]);
    const flags: Record<LessonTypeId, QueryFlag[]> = {};
    for (const type of types) flags[type.id] = lessonQueryFlags(type, catalog);
    return flags;
});

/** The dashboard's Consideration Queries card: approved types, and how many carry each flag. */
export const considerationQueryCountsState = atom(async (get) => {
    const [types, flags] = await Promise.all([get(considerationTypesState), get(considerationQueryFlagsState)]);
    return {
        approved: types.filter((type) => type.documentStatus === 'approved').length,
        flags: countQueryFlags(types, flags),
    };
});

// --- The current home (the home detail page) ---

const homeSystemInfoState = systemInfoDao.systemInfoListAtom(currentHomeIdState);
const homeCustomEquipmentTypesState = customEquipmentTypeDao.customEquipmentTypesAtom(currentHomeIdState);

/**
 * The catalog for queries typed against the current home: the global catalog plus the home's custom
 * types, which a custom reminder's query may name.
 */
export const homeHqlCatalogState = atom(async (get): Promise<HqlCatalog> => {
    const [catalog, customEquipmentTypes] = await Promise.all([
        get(hqlCatalogState),
        get(homeCustomEquipmentTypesState),
    ]);
    return { ...catalog, customEquipmentTypes };
});

/**
 * The current home's Home Document, built by `buildHomeDocument` as coverage (`HqlReader.fetchCoverageSources`)
 * builds it, with the home's custom types in the catalog.
 */
export const homeDocumentState = atom(async (get) => {
    const [home, equipment, systemInfo, catalog] = await Promise.all([
        get(currentHomeState),
        get(allHomeEquipmentState),
        get(homeSystemInfoState),
        get(homeHqlCatalogState),
    ]);
    if (!home) return undefined;
    return buildHomeDocument(
        {
            equipment,
            systemInfo,
            completedSpaceIds: finishedSpaceIds(home),
            timeZone: home.geo?.timezone,
        },
        catalog,
    );
});
